import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from "@hookform/resolvers/zod";

// Interfaces and helpers
import showToast from "@/_lib/toast";
import { ApiError } from "@/interfaces/api/error";
import { useFirstRender } from "@/_helpers/hooks";
import { ILoginCredentials, schema } from '@/interfaces/auth/login_credentials';

// Styles
import styles from './auth.module.scss';
import { East } from '@mui/icons-material';

// Services and selectors
import { useLoginMutation } from '@/repositories/auth';
import { selectIsAuthenticated } from "@/store/auth";

// Components
import { Box } from '@mui/material';
import EmailInput from '@/components/email_input';
import Button from '@/components/button';
import PasswordInput from '@/components/password_input';
import { ApiErrorEnum } from "@/_constants/api_errors";


const LoginPage: FC = () => {

    const { t } = useTranslation();
    const navigate = useNavigate();
    const isFirstRender = useFirstRender();

    const isAuthenticated = useSelector(selectIsAuthenticated)
    const [login, { isLoading }] = useLoginMutation();

    useEffect(() => {
        if (isAuthenticated) {
            console.log('isAuthenticated')
            navigate("/dashboard");
        }
    }, [isFirstRender, isAuthenticated, navigate]);

    const { control, handleSubmit, setError, formState } = useForm<ILoginCredentials>({
        resolver: zodResolver(schema),
        mode: 'onBlur',
        defaultValues: {
            email: '',
            password: ''
        }
    });

    const submitForm = async (data: ILoginCredentials) => {
        await login(data).unwrap().then(() => {
            navigate('/dashboard');
        }).catch((error: ApiError) => {
            if (error.type in ApiErrorEnum && error.type == 'auth_invalid_credentials') {
                setError('password', { type: 'api', message: t(`api.errors.${error.type}.message`)});
            } else if (error.type in ApiErrorEnum) {
                setError('email', { type: 'api', message: t(`api.errors.${error.type}.message`)});
            } else {
                console.log(error)
                showToast({type: 'error', apiError: error.type})
            }
        });
    }

    return (
        <Box className={styles.form} style={{width: '50%'}}>

            {/* title, subtitle and 40px spacer */}
            <span className="heading-05">{t('pages.login.title')}</span>
            <span className="body-01" style={{color: 'var(--text-secondary)'}}>{t('pages.login.subtitle')}</span>
            <Box height={40} />

            {/* Form */}
            <form onSubmit={handleSubmit(submitForm)} style={{width: '100%'}}>
                <EmailInput
                    name='email'
                    control={control}
                    label={t('inputs.titles.emailAddress')} 
                    placeHolder={t('inputs.placeholders.emailAddress')} 
                    tabIndex={1}
                    />
                <PasswordInput
                    name='password'
                    control={control}
                    label={t('inputs.titles.password')} 
                    placeHolder={t('inputs.placeholders.password')} 
                    tabIndex={2}
                    />
                
                <Box height={40} />

                {/* Actions */}
                <Box className={styles.actions} justifyContent="flex-end">
                    <Button
                        type="submit"
                        label={t('components.buttons.login')}
                        endIcon={<East />}
                        loading={isLoading}
                        disabled={isLoading || !formState.isValid}
                        onClick={handleSubmit(submitForm)}
                        />
                </Box>
            </form>
                
        </Box>
    )
}

export default LoginPage;

